import type {
    ComponentCategoryPayload,
    ComponentCategoryResponse,
} from '@/types/apps/component-category.type';
import type {
    ApiResponse,
    PaginatedResult,
    QueryParams,
} from '@/types/common/api.type';
import type { BaseApiClientInterface } from '@/types/service/base-api-client.interface';
import type { Creatable } from '@/types/service/creatable.interface';
import type { Deletable } from '@/types/service/deletable.interface';
import type { Exportable } from '@/types/service/exportable.interface';
import type { Importable } from '@/types/service/importable.interface';
import type { Indexable } from '@/types/service/indexable.interface';
import type { Showable } from '@/types/service/showable.interface';
import type { Updatable } from '@/types/service/updatable.interface';

type ComponentCategoryListResponse = ApiResponse<
    PaginatedResult<ComponentCategoryResponse>
>;

export class ComponentCategoryService
    implements
        Showable,
        Updatable,
        Deletable,
        Creatable,
        Indexable,
        Importable,
        Exportable
{
    public constructor(private apiClient: BaseApiClientInterface) {}

    public endpoints = {
        base: () => '/api/part-category',
        detail: (id: string) => `/api/part-category/${id}`,
        import: () => '/api/part-category/import',
        export: () => '/api/part-category/export',
    };

    public index(params?: QueryParams): Promise<ComponentCategoryListResponse> {
        return this.apiClient.fetchApi(this.endpoints.base(), params);
    }

    public detail(
        id: string,
        params?: QueryParams
    ): Promise<ApiResponse<ComponentCategoryResponse>> {
        const url = this.endpoints.detail(id);

        return this.apiClient.fetchApi(url, params);
    }

    public create(
        payload: ComponentCategoryPayload
    ): Promise<ApiResponse<ComponentCategoryResponse>> {
        return this.apiClient.postApi(this.endpoints.base(), payload);
    }

    public update(
        id: string,
        payload: ComponentCategoryPayload
    ): Promise<ApiResponse<ComponentCategoryResponse>> {
        return this.apiClient.putApi(this.endpoints.detail(id), payload);
    }
    public destroy(
        ids: string[],
        isHardDeleted: boolean = false
    ): Promise<ApiResponse<void>> {
        const url = this.endpoints.base();

        return this.apiClient.destroyApi<void>(url, ids, isHardDeleted);
    }

    public async importFile(file: File): Promise<any> {
        const form = this.apiClient.buildFormData({ file });

        return this.apiClient.postApi<any>(this.endpoints.import(), form);
    }

    public async exportFile(): Promise<Blob> {
        return this.apiClient.fetchBlob(this.endpoints.export());
    }
}
